import mixpanel from "mixpanel-browser";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import { Socket } from "socket.io-client";
import styled, { useTheme } from "styled-components";
import { routeList } from "../App";
import { lang } from "../assets/languages/en.language";
import { PlaneIcon } from "../components/svgs/icons/Plane.icon";
import { usePwaViewportWidth } from "../hooks/useScreenWidth";
import api, { Chat, ChatMessage, ChatParticipant } from "../services/api";
import { parseChatMessage } from "../services/chat.utils";
import socketService from "../services/socket.service";
import { useUserStore } from "../store/useUserStore";
import { RootContainer } from "../styles/Container.style";
import { Paragraph } from "../styles/Text";
import { Column, Gap, Row } from "../styles/Utils";

const messageLimit = 20;

export interface Message {
  id: string;
  isIncoming: boolean;
  content: string;
}

export default function ChatWithPage() {
  const theme = useTheme();
  const { chatId } = useParams();
  const viewportWidth = usePwaViewportWidth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [chat, setChat] = useState<Chat>();
  const [text, setText] = useState("");

  const fetchingOlderMessageRef = useRef(false);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const skip = useRef<number>(messageLimit);
  const lastScrollTop = useRef(0); // Store previous scroll position
  const [messages, setMessages] = useState<Message[]>([]);

  const { profile, setDogNameOnTopNavBar } = useUserStore();
  const userId = profile._id;

  const sender = useRef<ChatParticipant>();

  useEffect(() => {
    if (!chatId) return;

    // Recover last messages
    fetchChat(chatId);

    // Use Global Socket
    const newSocket = socketService.connect();
    if (newSocket) {
      setSocket(newSocket);
      newSocket.emit("joinChat", { chatId });
    }

    return () => {
      // We don't disconnect the global socket here anymore
      // just leave the "room" if necessary (optional depending on backend logic)
    };
  }, [chatId]);

  const fetchChat = async (chatId: string) => {
    try {
      const res = await api.getChat(chatId);
      const chat = res.data;
      setChat(chat);
      sender.current = chat?.participants.find((e) => e._id != profile._id);
      setDogNameOnTopNavBar(sender.current!.dogName);
    } catch (err) {
      console.log("Error fetching specific chat", err);
      alert("Error fetching specific chat");
    }
  };

  useEffect(() => {
    if (!socket) return;

    socket.on("chatHistory", handleChatHistory);
    socket.on("receiveMessage", handleReceiveMessage);
    socket.on("receiveOlderMessages", handleOlderMessages);

    return () => {
      // Cleanup listeners when component unmounts or chatId changes
      socket.off("chatHistory", handleChatHistory);
      socket.off("receiveMessage", handleReceiveMessage);
      socket.off("receiveOlderMessages", handleOlderMessages);
    };
  }, [socket]); // Re-run when chatId changes

  // Listen for chat history
  const handleChatHistory = (msgs: ChatMessage[]) => {
    // console.log(msgs);
    try {
      api.markMessagesAsRead(chatId!);
    } catch (err) {
      console.log("Error updating messages reading", err);
    }
    setMessages(msgs.map((e) => parseChatMessage(e, userId!)).reverse());
  };

  // Listen for new messages
  const handleReceiveMessage = (msg: ChatMessage) => {
    console.log("receiveMessage", msg);
    setMessages((prev) => [...prev, parseChatMessage(msg, userId!)]);
    skip.current++;
    setTimeout(() => {
      if (containerRef.current) {
        console.log("sroll down");
        containerRef.current.scrollTo({ top: containerRef.current.scrollHeight, behavior: "smooth" });
      }
    }, 0);
  };

  const handleOlderMessages = (msgs: ChatMessage[]) => {
    console.log("receiveOlderMessages", msgs);

    const prevScrollHeight = containerRef.current?.scrollHeight; // 🔹 Save scroll height before update

    if (msgs.length > 0) {
      setTimeout(() => {
        setMessages((prev) => [...msgs.map((e) => parseChatMessage(e, userId!)).reverse(), ...prev]);
        skip.current = skip.current + msgs.length;

        setTimeout(() => {
          const chatContainer = containerRef.current;
          // Restore scroll position after DOM update
          if (chatContainer && prevScrollHeight) {
            console.log(
              "Restore scroll position",
              chatContainer.scrollTop,
              prevScrollHeight,
              chatContainer.scrollHeight
            );
            chatContainer.scrollTop += chatContainer.scrollHeight - prevScrollHeight;
          }
        }, 0);

        setLoading(false);
        fetchingOlderMessageRef.current = false;
      }, 1000); // ⏳ Delay everything by 1 second
    } else {
      // If no messages, still wait before allowing a new fetch
      setTimeout(() => {
        fetchingOlderMessageRef.current = false;
        setLoading(false);
      }, 1000);
    }
  };

  const sendMessage = () => {
    if (text.trim()) {
      console.log("sendMessage");
      socket!.emit("sendMessage", { chatId, content: text });
      mixpanel.track("Send message");
      setText("");
      if (messageInputRef.current) {
        messageInputRef.current.style.height = "36px"; // Reset height manually
      }
    }
  };

  // Resize input according to number of line.
  const updateRowNumber = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    // console.log(e.target.scrollHeight);
    console.log(e.target.style.height);
    // Dynamically adjust height
    e.target.style.height = "36px"; // Reset to min-height
    e.target.style.height = `${e.target.scrollHeight < 100 ? e.target.scrollHeight : 95}px`; // Set new height
  };

  // Scroll down logic
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (containerRef.current && messages.length > 0 && messages.length <= messageLimit) {
      console.log("sroll down");
      containerRef.current.scrollTo({ top: containerRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  // Trigger historic loading
  const handleScroll = async () => {
    // console.log("containerRef.current.scrollHeight", containerRef.current?.scrollHeight);
    if (containerRef.current) {
      const scrollTop = containerRef.current.scrollTop;
      // console.log(scrollTop);
      // messages.length > 0 : we check that chatHistory gave back msgs
      // fetchingOlderMessageRef.current == false : We don't want to request messages several times
      if (
        scrollTop < 30 &&
        scrollTop < lastScrollTop.current &&
        fetchingOlderMessageRef.current == false &&
        messages.length >= 20
        // messages.length > 0
      ) {
        // console.log(scrollTop, loading, messages.length);
        console.log("loadOlderMessages");
        fetchingOlderMessageRef.current = true;
        setLoading(true);
        console.log("skip.current", skip.current);
        socket?.emit("loadOlderMessages", { chatId, skip: skip.current });
      }

      lastScrollTop.current = scrollTop;
    }
  };

  // Trigger message sending on "enter" keyboard press
  const triggerSendOnEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent new line
      sendMessage();
    }
  };

  const isEmpty = text.trim() === "";

  if (!socket || !chat || !sender)
    return (
      <Row $justifyContent="center">
        <BeatLoader color={theme.color.primaryDark} />
      </Row>
    );

  return (
    <RootContainerOvl $height="100%">
      <ColumnOvl $alignItem="center" ref={containerRef} $flexGrow={1} onScroll={handleScroll}>
        {loading && (
          <>
            <Gap $height={2} />
            <Row $justifyContent="center">
              <BeatLoader color={theme.color.primaryDark} />
            </Row>
          </>
        )}

        <Warning>
          <Paragraph>{lang.chatithPage.warningMessage}</Paragraph>
        </Warning>

        <Column $gap={2}>
          {messages.map((e) => (
            <MessageItem key={e.id} $isIncoming={e.isIncoming}>
              {e.isIncoming && (
                <Avatar
                  src={sender.current!.imageUrls[0]}
                  onClick={() => {
                    navigate(`${routeList.profileView}/${sender.current!._id}`);
                  }}
                />
              )}
              <Paragraph $color={theme.color.tertiaryLight}>{e.content}</Paragraph>
              {!e.isIncoming && <Avatar src={profile.imageUrls[0]} />}
              <Arrow $isIncoming={e.isIncoming} />
            </MessageItem>
          ))}
          <Gap $height={1} />
        </Column>
      </ColumnOvl>
      <InputBarContainer $width={viewportWidth}>
        <MessageInput
          rows={1}
          onChange={updateRowNumber}
          value={text}
          ref={messageInputRef}
          onKeyDown={triggerSendOnEnter}
          maxLength={500}
        />
        <SendButton $isEmpty={isEmpty} onClick={sendMessage} $backgroundColor={theme.color.primaryDark}>
          <PlaneIcon color={theme.color.tertiaryLight} />
        </SendButton>
      </InputBarContainer>
    </RootContainerOvl>
  );
}

const ColumnOvl = styled(Column)`
  overflow-y: scroll;
`;

const RootContainerOvl = styled(RootContainer)`
  align-items: center;
  overflow: hidden;
  padding: 0;
`;

const SendButton = styled.div<{ $backgroundColor: string; $isEmpty: boolean }>`
  border-radius: 100px;
  height: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  background-color: ${(props) => props.$backgroundColor};
  opacity: ${(props) => (props.$isEmpty ? "0.8" : "1")};
  padding: 0.6rem;
  height: 3rem;
  > svg {
    width: 100%;
  }
`;

const MessageInput = styled.textarea`
  resize: none;
  flex-grow: 1;
  background-color: ${(props) => props.theme.color.tertiaryLight};
  color: ${(props) => props.theme.color.primaryDark};
  font-size: ${(props) => props.theme.font.size.title.medium};
  padding: 0.6rem;
  border-radius: 100px;
`;

const InputBarContainer = styled(Row)<{
  $width: number;
}>`
  width: calc(${(props) => props.$width}px - 1rem);
  gap: 1rem;
  align-items: center;
  border-radius: 100px;
  padding: 0.5rem;
  background-color: ${(props) => props.theme.color.tertiaryLight};
`;

const Arrow = styled.div<{ $isIncoming: boolean }>`
  position: absolute;
  background-color: inherit;
  width: 3rem;
  aspect-ratio: 1;
  transform: rotateZ(45deg);
  bottom: -1rem;
  left: ${(props) => props.$isIncoming && "3rem"};
  right: ${(props) => !props.$isIncoming && "3rem"};
`;

const Avatar = styled.img`
  border-radius: 100px;
  width: 5rem;
  aspect-ratio: 1;
  z-index: 1;
`;

const MessageItem = styled(Row)<{ $isIncoming: boolean }>`
  position: relative;
  align-self: ${(props) => (props.$isIncoming ? "start" : "end")};
  background-color: ${(props) => props.theme.color.primaryDark};
  border-radius: 30px;
  padding: 1rem;
  /* width: 80%; */
  max-width: 80%;
  gap: 1rem;
  padding: 1.5rem;
  align-items: start;
  border: ${(props) => `3px solid ${props.theme.color.tertiaryLight}`};
  > span {
    flex-grow: 1;
    text-align: ${(props) => (props.$isIncoming ? "start" : "end")};
    /* word-break: break-all; */
  }
`;

const Warning = styled.p`
  padding: 2rem;
  background-color: ${(props) => props.theme.color.primary};
  border-radius: 30px;
  text-align: center;
  > span {
    color: ${(props) => props.theme.color.tertiaryLight};
  }
`;
